import React, { useEffect, useState } from "react";
import { BulkImportDialog } from "../../components/BulkImportDialog";
import { PhoneContactsImport } from "../../components/guests/PhoneContactsImport";
import { toastApiError } from "../../lib/upgrade";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Check, Search, ChevronRight, Crown, Upload } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const RSVP_BADGES = {
  attending: { label: "Attending", cls: "bg-[#e9f3e7] text-[#2f6f2f]" },
  not_attending: { label: "Declined", cls: "bg-[#f6e4e4] text-[#8a3535]" },
  pending: { label: "Pending", cls: "bg-[var(--bg-warm)] text-[var(--ink)]" },
  maybe: { label: "Maybe", cls: "bg-[#fff2d4] text-[#7a5b13]" },
};

export default function GuestsPage() {
  const [showImport, setShowImport] = useState(false);
  const [wedding, setWedding] = useState(null);
  const [guests, setGuests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", group: "Family", plus_one_allowed: false, kids_count: 0 });

  async function load() {
    const w = await api.get("/weddings/mine");
    setWedding(w.data);
    const g = await api.get(`/weddings/${w.data.wedding_id}/guests`);
    setGuests(g.data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function addGuest(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/weddings/${wedding.wedding_id}/guests`, form);
      setForm({ name: "", email: "", phone: "", group: "Family", plus_one_allowed: false, kids_count: 0 });
      setShowAdd(false);
      toast.success("Guest added");
      load();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  }

  async function update(g, patch) {
    try {
      await api.patch(`/guests/${g.guest_id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(id) {
    if (!confirm("Remove this guest?")) return;
    await api.delete(`/guests/${id}`);
    load();
  }

  const filtered = guests.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));
  const counts = {
    all: guests.length,
    attending: guests.filter((g) => g.rsvp_status === "attending").length,
    pending: guests.filter((g) => g.rsvp_status === "pending").length,
    declined: guests.filter((g) => g.rsvp_status === "not_attending").length,
  };

  return (
    <div data-testid="guests-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Guest List</p>
          <h1 className="font-serif font-light text-4xl">Manage your guests</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <PhoneContactsImport guests={guests} onSaved={() => load().catch(() => {})} />
          <button data-testid="guests-bulk-import-btn" onClick={() => setShowImport(true)} className="btn-outline-gold"><Upload className="w-4 h-4" /> Bulk upload</button>
          <button data-testid="add-guest-btn" onClick={() => setShowAdd(true)} className="btn-gold"><Plus className="w-4 h-4" /> Add Guest</button>
        </div>
        <BulkImportDialog open={showImport} onClose={() => setShowImport(false)} defaultKind="guest" hasWedding onSaved={() => load().catch(() => {})} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total", v: counts.all },
          { label: "Attending", v: counts.attending },
          { label: "Pending", v: counts.pending },
          { label: "Declined", v: counts.declined },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-[var(--line)] rounded-2xl p-5">
            <p className="font-serif text-3xl">{s.v}</p>
            <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl">
        <div className="px-5 py-4 border-b border-[var(--line)] flex items-center gap-3">
          <Search className="w-4 h-4 text-[var(--muted)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search guests…" className="flex-1 outline-none text-sm bg-transparent" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Contact</th>
                <th className="px-5 py-3">Group</th>
                <th className="px-5 py-3">+1 / Kids</th>
                <th className="px-5 py-3">Table</th>
                <th className="px-5 py-3">RSVP</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => {
                const b = RSVP_BADGES[g.rsvp_status] || RSVP_BADGES.pending;
                return (
                  <tr key={g.guest_id} className="border-t border-[var(--line)] hover:bg-[#EDE3D3]/40 transition" data-testid={`guest-row-${g.guest_id}`}>
                    <td className="px-5 py-4 font-medium">
                      <Link to={`/dashboard/guests/${g.guest_id}`} className="text-[var(--ink)] hover:text-[var(--gold)] hover:underline flex items-center gap-2" data-testid={`guest-name-link-${g.guest_id}`}>
                        {g.is_wedding_party && <Crown className="w-3 h-3 text-[var(--gold)]" />}
                        {g.name}
                        <ChevronRight className="w-3 h-3 opacity-60" />
                      </Link>
                    </td>
                    <td className="px-5 py-4 text-[var(--muted)]">{g.email || g.phone || "—"}</td>
                    <td className="px-5 py-4">{g.group}</td>
                    <td className="px-5 py-4">{g.plus_one_allowed ? "Yes" : "No"} / {g.kids_count}</td>
                    <td className="px-5 py-4">
                      <input value={g.table || ""} onChange={(e) => update(g, { table: e.target.value })} className="bg-transparent border-b border-transparent focus:border-[var(--gold)] text-sm outline-none w-20" />
                    </td>
                    <td className="px-5 py-4">
                      <select data-testid={`rsvp-${g.guest_id}`} value={g.rsvp_status} onChange={(e) => update(g, { rsvp_status: e.target.value })} className={`text-xs px-2 py-1 rounded-full ${b.cls} outline-none`}>
                        <option value="pending">Pending</option>
                        <option value="attending">Attending</option>
                        <option value="not_attending">Declined</option>
                        <option value="maybe">Maybe</option>
                      </select>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => remove(g.guest_id)} className="text-[var(--muted)] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-12 text-center text-[var(--muted)]">No guests yet. Add your first guest to start tracking RSVPs.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="font-script text-[var(--gold)] text-2xl">Add a guest</p>
                <h3 className="font-serif text-2xl">New Invitation</h3>
              </div>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={addGuest} className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Full Name</label>
                <input data-testid="form-guest-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-luxe" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-luxe" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-luxe" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Group</label>
                  <select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} className="input-luxe">
                    <option>Family</option><option>Friends</option><option>Wedding Party</option><option>Work</option><option>VIP</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Kids</label>
                  <input type="number" min="0" value={form.kids_count} onChange={(e) => setForm({ ...form, kids_count: parseInt(e.target.value || 0) })} className="input-luxe" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[var(--muted)]">
                <input type="checkbox" checked={form.plus_one_allowed} onChange={(e) => setForm({ ...form, plus_one_allowed: e.target.checked })} />
                Plus-one allowed
              </label>
              <button type="submit" data-testid="form-guest-submit" disabled={busy} className="btn-gold w-full">{busy ? "Adding…" : "Add Guest"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
